// An owned component (section 6.4): the inline sentence — "None" with Add (pick the component template or a heir, a new
// draft appears inline), or the component's fields rendered inline with ✕ that clears the reference and stages the
// component's deletion when nothing else references it.
import { useEffect, useMemo, useState } from 'react';
import { ops } from '../../api/operations';
import type { Document, Json, Template } from '../../api/types';
import { Button, IconButton } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { Field } from '../../ui/Field';
import { ExternalLink, Plus, X } from '../../ui/icons';
import { Select } from '../../ui/inputs';
import { useWorkspaceVersion } from '../../workspace/react';
import { entityKey, isTempId } from '../../workspace/Workspace';
import { FieldWidget } from './FieldWidget';
import { parameterHelp } from '../help';
import { createDocumentDraft, targetsOf, useWidgetEnv, type WidgetProps } from './context';

export function ComponentField({ param: p, value, onCommit, disabled, id }: WidgetProps) {
  const { catalog, workspace, client, org, game, branch, host } = useWidgetEnv();
  useWorkspaceVersion(workspace);
  const targets = useMemo(() => targetsOf(catalog, p), [catalog, p]);
  const [pickTemplate, setPickTemplate] = useState<string>(targets[0]?.name ?? '');
  const current = typeof value === 'string' ? value : null;
  const [fetched, setFetched] = useState<Document | null>(null);
  const draft = current ? workspace.getDraft(entityKey('document', current)) : undefined;
  const doc = (draft?.current as unknown as Document | undefined) ?? (fetched && fetched.id === current ? fetched : null) ?? (current ? (catalog.resolve(current) as Document | undefined) : undefined) ?? null;

  useEffect(() => {
    if (!current || isTempId(current) || draft || doc) return;
    let alive = true;
    client
      .data(ops.getDocument, { path: { org, game, branch, document: current } })
      .then((d) => alive && setFetched(d))
      .catch(() => alive && setFetched(null));
    return () => {
      alive = false;
    };
  }, [current, draft, doc, client, org, game, branch]);

  const add = () => {
    const t = catalog.templates.get(pickTemplate);
    if (!t) return;
    onCommit(createDocumentDraft(workspace, t));
  };
  const clear = async () => {
    const owned = current;
    onCommit(null);
    if (!owned) return;
    if (isTempId(owned)) {
      workspace.remove('document', owned);
      return;
    }
    try {
      const refs = await client.data(ops.listDocumentReferences, { path: { org, game, branch, document: owned }, query: { limit: 5 } });
      if (refs.items.length <= 1 && doc) workspace.remove('document', owned, { version: Number(doc.version), value: doc as unknown as Json }, { label: catalog.label(doc), template: doc.template });
    } catch {
      // the reference is cleared; the component stays until a later cleanup
    }
  };
  const template: Template | undefined = doc ? catalog.templates.get(doc.template) : undefined;

  if (!current) {
    return (
      <span className="row wrap component-field" id={id}>
        <span className="muted">None</span>
        {!disabled && targets.length > 1 && (
          <Select size="s" value={pickTemplate} onChange={(e) => setPickTemplate(e.target.value)} aria-label={`Component template for ${p.displayName ?? p.name}`}>
            {targets.map((t) => (
              <option key={t.name} value={t.name}>
                {t.displayName ?? t.name}
              </option>
            ))}
          </Select>
        )}
        {!disabled && (
          <Button size="s" onClick={add} disabled={!pickTemplate} icon={<Plus aria-hidden="true" />}>
            Add
          </Button>
        )}
      </span>
    );
  }
  if (!doc) {
    return (
      <span className="row component-field" id={id}>
        {isTempId(current) ? (
          <span className="muted">new component</span>
        ) : (
          <span className="loading-line" role="status">
            <span className="spinner" aria-hidden="true" />
            Loading…
          </span>
        )}
      </span>
    );
  }
  const data = (doc.data ?? {}) as Record<string, Json>;
  const base = isTempId(doc.id) ? undefined : { version: Number(doc.version), value: (fetched?.id === doc.id ? fetched : doc) as unknown as Json };
  const editField = (path: string, v: Json) => {
    workspace.edit('document', doc.id, path, v, { base, label: catalog.label(doc), template: doc.template });
  };
  return (
    <div className="component-field inline-group" id={id} data-testid={`component-${p.name}`}>
      <div className="row inline-group-head">
        <Badge tone="accent">{template?.displayName ?? doc.template.split('.').pop()}</Badge>
        {draft && <Badge tone={draft.status === 'new' ? 'success' : 'warning'}>{draft.status === 'new' ? 'New' : 'Modified'}</Badge>}
        <span className="spacer" />
        {host && (
          <IconButton label="Open the component" onClick={() => void host.open(doc.id)}>
            <ExternalLink aria-hidden="true" />
          </IconButton>
        )}
        {!disabled && (
          <IconButton label={`Remove ${p.displayName ?? p.name}`} onClick={() => void clear()}>
            <X aria-hidden="true" />
          </IconButton>
        )}
      </div>
      {(template?.effectiveParameters ?? []).map((f) => (
        <Field key={f.name} label={f.displayName ?? f.name} help={parameterHelp(f, doc, catalog.templates)} inline>
          <FieldWidget param={f} value={data[f.name]} disabled={disabled} owner={doc.id} onCommit={(v) => editField(`data.${f.name}`, v)} onCommitAt={(rel, v) => editField(rel.startsWith('[') ? `data.${f.name}${rel}` : `data.${f.name}.${rel}`, v)} />
        </Field>
      ))}
    </div>
  );
}
